/**
 * Image compression utility for WPPConnect media sending
 * WPPConnect server has ~100KB body-parser limit
 * Base64 encoding increases size by ~33%, so we target 60KB (→ ~80KB base64)
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeKB?: number;
}

export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<Blob> {
  const {
    maxWidth = 1600,
    maxHeight = 1600,
    quality = 0.65,
    maxSizeKB = 60  // Target 60KB because base64 adds ~33% (→ ~80KB)
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      
      // Scale down if necessary
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      
      ctx.drawImage(img, 0, 0, width, height);
      
      // Compress iteratively until under maxSizeKB
      let currentQuality = quality;
      let currentWidth = width;
      let currentHeight = height;
      
      const compress = () => {
        canvas.toBlob(
          (result) => {
            if (!result) {
              reject(new Error('Failed to compress image'));
              return;
            }
            
            const sizeKB = result.size / 1024;
            console.log(`[compressImage] Size: ${sizeKB.toFixed(1)}KB, Quality: ${currentQuality.toFixed(2)}, Dimensions: ${currentWidth}x${currentHeight}`);
            
            if (sizeKB > maxSizeKB && currentQuality > 0.1) {
              // Reduce quality first
              currentQuality -= 0.1;
              compress();
            } else if (sizeKB > maxSizeKB && currentWidth > 800) {
              // If still too large, reduce dimensions
              currentWidth = Math.round(currentWidth * 0.8);
              currentHeight = Math.round(currentHeight * 0.8);
              canvas.width = currentWidth;
              canvas.height = currentHeight;
              ctx.drawImage(img, 0, 0, currentWidth, currentHeight);
              currentQuality = quality; // Reset quality
              compress();
            } else {
              resolve(result);
            }
          },
          'image/jpeg',
          currentQuality
        );
      };
      
      compress();
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function isCompressibleImage(mimetype: string): boolean {
  return ['image/jpeg', 'image/png', 'image/webp', 'image/bmp', 'image/gif'].includes(mimetype);
}

// Re-export from centralized file validation
export { FILE_SIZE_LIMITS, getFileSizeLimit, validateFileSize } from './fileValidation';

// Maximum size in KB for image compression target (8MB is a good balance)
// This is used for compressing large images before upload
export const WPPCONNECT_MAX_SIZE_KB = 8 * 1024; // 8 MB
